// RadarTickHealth (TASK-921) — one line saying whether the Change Radar is actually running.
//
// A dead Radar and a quiet Radar draw the identical picture: an empty Signals rail. On
// 2026-09-04 the 15-minute tick had been raising for every account in the book, and every
// surface an advisor can see said "nothing to report". So the surfaces that present the
// Radar's OUTPUT carry the health of the thing that produced it, next to the output itself.
//
// Two independent facts, deliberately not folded together:
//   • last successful tick — when the sweep last COMPLETED (not when it last ran);
//   • failing since       — when the failures began; absent while the tick is healthy.
// A recent success with no failure marker is fine. A failure marker with a stale success is
// "running and getting nowhere", which is the state that used to be invisible.
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { httpClient } from '../../../lib/httpClient';

export interface RadarTickHealthResponse {
  last_success_at: string | null;
  failing_since: string | null;
  /** False when the API could not reach Redis — "unknown", never "never ticked". */
  available: boolean;
}

function when(iso: string | null): string {
  if (!iso) return 'never';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'unknown';
  const mins = Math.round((Date.now() - at.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return at.toLocaleDateString();
}

export function RadarTickHealth() {
  const { data, isLoading } = useQuery<RadarTickHealthResponse>({
    queryKey: ['radar-tick-health'],
    queryFn: () => httpClient.get<RadarTickHealthResponse>('/api/radar/health'),
    // The tick runs every 15 minutes; polling faster only spends requests.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) return null;

  // Unreachable Redis is a gap in the ANSWER, not a verdict about the tick. Say so.
  if (!data || !data.available) {
    return (
      <p className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="radar-tick-health">
        <HelpCircle className="h-3 w-3" />
        Radar sweep status unavailable — an empty list here proves nothing.
      </p>
    );
  }

  if (data.failing_since) {
    return (
      <p className="flex items-center gap-1 text-[11px] font-medium text-red-700 dark:text-red-400" data-testid="radar-tick-health">
        <AlertTriangle className="h-3 w-3" />
        Radar sweep failing since {when(data.failing_since)} — last successful tick{' '}
        {when(data.last_success_at)}. Signals below are stale, not quiet.
      </p>
    );
  }

  return (
    <p className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="radar-tick-health">
      <CheckCircle2 className="h-3 w-3" />
      Last successful Radar sweep {when(data.last_success_at)}.
    </p>
  );
}
